import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { X } from 'lucide-react';
import { useState } from 'react';
import { PersonAvatar } from '@/shared/components/PersonAvatar';
import { useRmsQuery } from '../hooks/useSubscriberQueries';
import { SHARED } from '../model/byOwner';
import { outlineButtonSx } from './styles';

type Kind = 'topic' | 'client_book';
export type ListForm = { name: string; description: string; ownerId?: number | undefined };

type Props = {
  mode: 'new' | 'rename';
  initial?: { name: string; description: string } | undefined;
  /** An admin may make topic lists and client books for any RM; an RM only their own client book. */
  isAdmin: boolean;
  /** The signed-in account is really an admin, not viewing as one: only that account can read the RMs to pick from. */
  accountIsAdmin: boolean;
  /** When an admin manages one RM's lists, new lists are that RM's. */
  fixedOwner?: { id: number; name: string } | undefined;
  myName: string;
  isPending: boolean;
  error: string | null;
  onCancel: () => void;
  onSave: (form: ListForm) => void;
};

function KindChoice({ kind, onKind, canTopic, canBook }: { kind: Kind; onKind: (k: Kind) => void; canTopic: boolean; canBook: boolean }) {
  const options: [Kind, string, string, string | null][] = [
    ['topic', 'Topic list', 'Research by theme, e.g. FX or Rates', canTopic ? null : 'Admins make topic lists'],
    ['client_book', 'Client book', "One RM's clients", canBook ? null : 'Needs a real admin account'],
  ];
  return (
    <Box role="radiogroup" aria-label="Kind of list" sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1 }}>
      {options.map(([k, title, hint, why]) => {
        const isOn = kind === k;
        const disabled = why !== null;
        return (
          <ButtonBase key={k} role="radio" aria-checked={isOn} disabled={disabled} onClick={() => { onKind(k); }} sx={optionSx(isOn, disabled)}>
            <Box sx={radioSx(isOn)} aria-hidden />
            <Box sx={{ textAlign: 'left' }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{title}</Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>{why ?? hint}</Typography>
            </Box>
          </ButtonBase>
        );
      })}
    </Box>
  );
}

function OwnerField({ kind, isAdmin, fixedOwner, myName, ownerId, onOwner }: { kind: Kind; isAdmin: boolean; fixedOwner: Props['fixedOwner']; myName: string; ownerId: number | ''; onOwner: (id: number) => void }) {
  const rmsQuery = useRmsQuery(isAdmin && kind === 'client_book' && !fixedOwner);
  const rms = rmsQuery.data ?? [];
  const fixed = kind === 'topic' ? SHARED : (fixedOwner?.name ?? (isAdmin ? null : myName));
  if (fixed) {
    return (
      <TextField label="Owner" value={fixed} disabled slotProps={{ input: { startAdornment: <Box sx={{ mr: 1, display: 'flex' }}><PersonAvatar name={fixed} size={20} /></Box> } }} />
    );
  }
  return (
    <TextField select required label="Owner" value={ownerId} error={rmsQuery.isError} onChange={(e) => { onOwner(Number(e.target.value)); }} helperText={rmsQuery.isError ? 'Could not load the RMs. Close this and try again.' : 'The RM whose clients these are'}>
      {rms.map((r) => <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>)}
    </TextField>
  );
}

type Check = { isNew: boolean; isAdmin: boolean; kind: Kind; fixedOwner: Props['fixedOwner']; name: string; ownerId: number | '' };

/** Whether the form can be saved, and the RM it is for: an admin's new client book needs one picked. */
function checkForm({ isNew, isAdmin, kind, fixedOwner, name, ownerId }: Check) {
  const needsOwner = isNew && isAdmin && kind === 'client_book' && !fixedOwner;
  const picked = ownerId === '' ? undefined : ownerId;
  return { valid: name.trim().length >= 2 && (!needsOwner || picked !== undefined), owner: fixedOwner?.id ?? (needsOwner ? picked : undefined) };
}

function Foot({ isNew, ready, onCancel, onSave }: { isNew: boolean; ready: boolean; onCancel: () => void; onSave: () => void }) {
  return (
    <Stack direction="row" sx={{ justifyContent: 'flex-end', gap: 1, px: 2.5, py: 1.5, borderTop: 1, borderColor: 'divider' }}>
      <Button variant="outlined" onClick={onCancel} sx={outlineButtonSx}>Cancel</Button>
      <Button variant="contained" disabled={!ready} onClick={onSave}>{isNew ? 'Create list' : 'Save'}</Button>
    </Stack>
  );
}

function Head({ isNew, onCancel }: { isNew: boolean; onCancel: () => void }) {
  return (
    <Stack direction="row" sx={{ alignItems: 'flex-start', px: 2.5, pt: 2.5, pb: 1.5 }}>
      <Box sx={{ flexGrow: 1 }}>
        <Typography id="list-dialog-title" variant="h4" component="h2">{isNew ? 'Create a list' : 'Rename list'}</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>Lists decide who receives an approved WhatsApp message.</Typography>
      </Box>
      <IconButton size="small" aria-label="Close" onClick={onCancel}><X size={18} /></IconButton>
    </Stack>
  );
}

/** Make a list, or rename one: what kind, its name, who owns it, and a line on what it receives. */
export function ListDialog({ mode, initial, isAdmin, accountIsAdmin, fixedOwner, myName, isPending, error, onCancel, onSave }: Props) {
  const [kind, setKind] = useState<Kind>(() => (isAdmin && !fixedOwner ? 'topic' : 'client_book'));
  const [name, setName] = useState(() => initial?.name ?? '');
  const [description, setDescription] = useState(() => initial?.description ?? '');
  const [ownerId, setOwnerId] = useState<number | ''>('');
  const isNew = mode === 'new';
  const { valid, owner } = checkForm({ isNew, isAdmin, kind, fixedOwner, name, ownerId });

  return (
    <Dialog open onClose={onCancel} fullWidth maxWidth="sm" aria-labelledby="list-dialog-title" slotProps={{ paper: { sx: { borderRadius: 2 } } }}>
      <Head isNew={isNew} onCancel={onCancel} />
      <Stack sx={{ gap: 2, px: 2.5, pb: 2.5 }}>
        {isNew ? <KindChoice kind={kind} onKind={setKind} canTopic={isAdmin && !fixedOwner} canBook={!isAdmin || Boolean(fixedOwner) || accountIsAdmin} /> : null}
        <TextField required label="Name" value={name} onChange={(e) => { setName(e.target.value); }} slotProps={{ htmlInput: { maxLength: 60 } }} />
        {isNew ? <OwnerField kind={kind} isAdmin={isAdmin} fixedOwner={fixedOwner} myName={myName} ownerId={ownerId} onOwner={setOwnerId} /> : null}
        <TextField label="Description (optional)" placeholder="What this list receives" multiline minRows={2} value={description} onChange={(e) => { setDescription(e.target.value); }} slotProps={{ htmlInput: { maxLength: 120 } }} />
        {error ? <Typography variant="body2" role="alert" sx={{ color: 'error.main' }}>{error}</Typography> : null}
      </Stack>
      <Foot isNew={isNew} ready={valid && !isPending} onCancel={onCancel} onSave={() => { onSave({ name: name.trim(), description: description.trim(), ownerId: owner }); }} />
    </Dialog>
  );
}

const optionSx = (isOn: boolean, disabled: boolean) => ({
  justifyContent: 'flex-start',
  alignItems: 'flex-start',
  gap: 1.25,
  p: 1.5,
  borderRadius: 1.5,
  border: 1,
  borderColor: isOn ? 'primary.main' : 'divider',
  bgcolor: isOn ? 'surface.accentSoft' : 'background.paper',
  opacity: disabled ? 0.55 : 1,
  transition: 'border-color 150ms, background-color 150ms',
});

const radioSx = (isOn: boolean) => ({
  width: 16,
  height: 16,
  mt: 0.25,
  flexShrink: 0,
  borderRadius: '50%',
  border: isOn ? 5 : 1.5,
  borderColor: isOn ? 'primary.main' : 'surface.borderStrong',
  transition: 'border-width 150ms',
});
